import type {ExtendedInfinityErrorCode} from '@pexip/infinity';

/** Stable native service-discovery failure codes. */
export type ServiceDiscoveryErrorCode =
    | 'invalid_hostname'
    | 'nxdomain'
    | 'no_srv_records'
    | 'no_valid_srv_records'
    | 'timeout'
    | 'resolver_failure'
    | 'unauthorized_webview'
    | 'managed_configuration_unavailable';

const SERVICE_DISCOVERY_ERROR_CODES: ReadonlySet<string> = new Set([
    'invalid_hostname',
    'nxdomain',
    'no_srv_records',
    'no_valid_srv_records',
    'timeout',
    'resolver_failure',
    'unauthorized_webview',
    'managed_configuration_unavailable',
]);

/**
 * Checks whether an untrusted IPC rejection is a stable discovery error code.
 *
 * @param value - Rejection value received from Tauri.
 * @returns Whether the value belongs to the service-discovery contract.
 *
 * @example
 * isServiceDiscoveryErrorCode('nxdomain'); // true
 */
export const isServiceDiscoveryErrorCode = (
    value: unknown,
): value is ServiceDiscoveryErrorCode =>
    typeof value === 'string' && SERVICE_DISCOVERY_ERROR_CODES.has(value);

/** Typed failure raised by the native service-discovery adapter. */
export class ServiceDiscoveryError extends Error {
    public override readonly name = 'ServiceDiscoveryError';

    /**
     * Creates a discovery failure without exposing resolver implementation
     * details or untrusted hostname input.
     *
     * @param code - Stable failure code crossing the native IPC boundary.
     *
     * @example
     * throw new ServiceDiscoveryError('nxdomain');
     */
    public constructor(public readonly code: ServiceDiscoveryErrorCode) {
        super(`Service discovery failed: ${code}`);
    }
}

/**
 * Maps native discovery failures onto existing user-facing call errors.
 *
 * @param code - Stable discovery failure code.
 * @returns Existing Infinity error code shown by the meeting error page.
 *
 * @example
 * getServiceDiscoveryInfinityErrorCode('timeout'); // '#pex111'
 */
export const getServiceDiscoveryInfinityErrorCode = (
    code: ServiceDiscoveryErrorCode,
): ExtendedInfinityErrorCode => {
    switch (code) {
        case 'invalid_hostname':
        case 'nxdomain':
        case 'no_srv_records':
        case 'no_valid_srv_records':
            return '#pex154';
        case 'timeout':
        case 'resolver_failure':
            return '#pex111';
        case 'managed_configuration_unavailable':
        case 'unauthorized_webview':
            return '#pex116';
    }
};

/** Resolves a logical Infinity hostname to ordered HTTPS targets. */
export interface ServiceDiscovery {
    /** Read the hostname supplied by managed native deployment configuration. */
    getDefaultHostname: () => Promise<string>;
    /** Resolve the hostname to validated, priority/weight-ordered targets. */
    lookup: (hostname: string) => Promise<string[]>;
}

/** Minimum call routing fields transformed by native service discovery. */
export interface CallDestination {
    /** Conference alias, optionally suffixed with a native hostname override. */
    conferenceAlias: string;
    /** Explicit API origin selected for the call. */
    host?: string;
}

/**
 * Returns the explicit hostname of a native `<conference-alias>@<hostname>`
 * destination, split on the final `@`.
 *
 * @param destination - Conference alias as entered by the user.
 * @returns The hostname suffix, or `undefined` when there is none.
 *
 * @example
 * getExplicitHostname('meet@vc.example.com'); // 'vc.example.com'
 */
const getExplicitHostname = (destination: string): string | undefined => {
    const separatorIndex = destination.lastIndexOf('@');
    if (separatorIndex < 0) {
        return undefined;
    }

    const conferenceAlias = destination.slice(0, separatorIndex);
    const hostname = destination.slice(separatorIndex + 1);
    if (!conferenceAlias || !hostname) {
        throw new TypeError('Invalid native call destination');
    }
    return hostname;
};

export const createHttpsOrigin = (target: string): string => {
    if (!target || target.trim() !== target) {
        throw new TypeError('Service discovery returned an invalid target');
    }
    const authority = target.includes(':') ? `[${target}]` : target;
    const url = new URL(`https://${authority}`);
    if (
        url.hostname !== authority ||
        url.protocol !== 'https:' ||
        url.username ||
        url.password ||
        url.port ||
        url.pathname !== '/' ||
        url.search ||
        url.hash
    ) {
        throw new TypeError('Service discovery returned an invalid target');
    }
    return url.origin;
};

/**
 * Resolves native call routing while leaving browser calls unchanged.
 *
 * The conference alias is passed to Infinity unchanged, including any
 * `@<hostname>` suffix, because Infinity uses it for routing. The suffix only
 * selects which deployment to discover.
 *
 * @param options - Call arguments and optional native discovery dependency.
 * @returns Call arguments and the node selected for common call configuration.
 *
 * @example
 * await resolveCallDestination({callArgs, serviceDiscovery});
 */
export const resolveCallDestination = async <T extends CallDestination>({
    callArgs,
    serviceDiscovery,
}: {
    callArgs: T;
    serviceDiscovery?: ServiceDiscovery;
}): Promise<{callArgs: T; node?: string}> => {
    if (!serviceDiscovery) {
        return {callArgs};
    }

    const hostname =
        getExplicitHostname(callArgs.conferenceAlias) ??
        (await serviceDiscovery.getDefaultHostname());
    const targets = await serviceDiscovery.lookup(hostname);
    const target = targets[0];
    if (!target) {
        throw new Error('Service discovery returned no targets');
    }

    return {
        callArgs: {...callArgs, host: createHttpsOrigin(target)},
        node: target,
    };
};
